/**
 * @fileoverview Component for displaying a table of studies.
 * This component includes a header with columns and a body with study data.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import StudyIcon from '../../assets/menu/studies.svg'

const columns = ['Name', 'Site', 'Screened']

/**
 * @interface StudyListTableProps
 * @description Props for the StudyListTable component.
 * @property {Novoic.API.Study[]} data - The data to display in the table.
 * @property {(e: unknown, row: string) => void} onRowClicked - The function to call when a row is clicked.
 */
interface StudyListTableProps {
    data: Novoic.API.Study[]
    onRowClicked: (e: unknown, row: string) => void
}

/**
 * @function StudyListTable
 * @description Component for displaying a table of studies.
 * @param {StudyListTableProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the table of studies.
 */
export function StudyListTable({ data, onRowClicked }: StudyListTableProps) {
    return (
        <table className="w-full">
            <thead className="nv-thead top-[161px] sticky">
                <tr className="h-12">
                    {columns.map((col, index) => (
                        <th
                            key={col}
                            scope="col"
                            className={`${
                                index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                            } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                        >
                            <button className="group inline-flex">
                                {col}
                                <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                            </button>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((study: Novoic.API.Study) => (
                    <tr
                        key={study.id}
                        className="hover:bg-[#f7f9ff] cursor-pointer transition-all"
                        onClick={(e) => onRowClicked(e, study.id)}
                    >
                        <td
                            className="pl-8 flex items-center gap-x-2 whitespace-nowrap pr-10 py-4"
                            role="presentation"
                        >
                            <div className="flex shrink-0 justify-center items-center w-8 h-8 rounded-full bg-indigo-50">
                                <StudyIcon className="fill-indigo-500 stroke-indigo-50" />
                            </div>
                            <p className="font-medium text-sm">{study.name}</p>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            <p className="font-medium text-sm">{study.sites_count}</p>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            <p className="font-medium text-sm">{study.screened_count}</p>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
